import axios, { AxiosResponse } from "axios";
import { Values } from "./Values";
import Stores from "@src/Stores";
import { Toast } from "./Toast";
import _ from "lodash";

export type AxiosCustomErrorProcessor = (error: any) => boolean;

export default class RequestsProcessor {
  static requestsCounter: number = 0;
  static pageReloadScheduledAttempts: number = 0;
  static pageReloadScheduled: boolean = false;

  api = axios.create({
    baseURL: `${window.location.origin}/api/`,
    withCredentials: true
  });
  toast: Toast = new Toast();

  _getRequestFailedString(): string {
    return Values.getLocalizationValue("requestFailed", "Request failed");
  }

  async get<T>(
    query: string,
    comment: string = "",
    isShowMessage: boolean = true,
    customErrorProcessor?: AxiosCustomErrorProcessor,
    silentRequest: boolean = false
  ): Promise<T> {
    return await this.request<T>(async () => {
      let result = await this.api.get<T>(query);
      if (result.status === 200 || 204) {
        console.log(`${comment}:`, result.data);
      } else {
        result = null;
      }
      return result;
    }, comment, isShowMessage, customErrorProcessor, silentRequest);
  }
  async delete<T>(
    query: string,
    comment: string = "",
    isShowMessage: boolean = true,
    customErrorProcessor?: AxiosCustomErrorProcessor,
    silentRequest: boolean = false
  ): Promise<T> {
    return await this.request<T>(async () => {
      let result = await this.api.delete<T>(query);
      if (result.status === 200 || 204) {
        console.log(`${comment}:`, result.data);
      } else {
        result = null;
      }
      return result;
    }, comment, isShowMessage, customErrorProcessor, silentRequest);
  }

  async post<T>(
    query: string,
    comment: string = "",
    params: any = {},
    isShowMessage: boolean = true,
    customErrorProcessor?: AxiosCustomErrorProcessor,
    silentRequest: boolean = false
  ): Promise<T> {
    return await this.request<T>(async () => {
      let result = await this.api.post(query, params);
      console.log(`${comment}:`, result.data);
      return result;
    }, comment, isShowMessage, customErrorProcessor, silentRequest);
  }
  async put<T>(
    query: string,
    comment: string = "",
    params: any = {},
    isShowMessage: boolean = true,
    customErrorProcessor?: AxiosCustomErrorProcessor,
    silentRequest: boolean = false
  ): Promise<T> {
    return await this.request<T>(async () => {
      let result = await this.api.put(query, params);
      console.log(`${comment}:`, result.data);
      return result;
    }, comment, isShowMessage, customErrorProcessor, silentRequest);
  }
  async patch<T>(
    query: string,
    comment: string = "",
    params: any = {},
    isShowMessage: boolean = true,
    customErrorProcessor?: AxiosCustomErrorProcessor,
    silentRequest: boolean = false
  ): Promise<T> {
    return await this.request<T>(async () => {
      let result = await this.api.patch(query, params);
      console.log(`${comment}:`, result.data);
      return result;
    }, comment, isShowMessage, customErrorProcessor, silentRequest);
  }


  private async request<T>(
    query: () => Promise<AxiosResponse<T>>,
    comment: string,
    isShowMessage: boolean,
    customErrorProcessor?: AxiosCustomErrorProcessor,
    silentRequest: boolean = false
  ): Promise<T> {
    let result: AxiosResponse<T> | null = null;
    RequestsProcessor.requestsCounterInc(silentRequest);

    try {
      if (isShowMessage && !silentRequest) {
        Stores.values.handleRequestProcess(true);
      }
      result = await query();
      RequestsProcessor.requestsCounterDec(silentRequest);
    } catch (error) {
      RequestsProcessor.requestsCounterDec(silentRequest);
      let handled = _.isFunction(customErrorProcessor) && customErrorProcessor(error);
      if (!handled && error.response && error.response.status === 401) {
        window.location.reload();
      }
      if (!handled && isShowMessage) {
        if (!error.response) {
          this.toast.showError(error.message);
          return;
        }
        const responseData = error.response.data;
        if (typeof responseData === 'object' && responseData !== null && responseData.Message) {
          this.toast.showError(responseData.Message);
        } else {
          this.toast.showError(`${this._getRequestFailedString()}: ${comment}`);
        }
      }
      result = null;
    }

    if (!silentRequest) {
      Stores.values.handleRequestProcess(false);
    }

    if (result && result.data) {
      _.defer(() => this.checkCustomHeaders<T>(result));
      return result.data;
    } else {
      return null;
    }
  }

  private async checkCustomHeaders<T>(result: AxiosResponse<T>): Promise<void> {
    if (result && result.headers) {
      let hasTokenRefreshedHeader = _.some(_.keys(result.headers), h => {
        return _.toUpper(h) == _.toUpper(Stores.values.common.TokenRefreshedHeaderKey);
      });
      if (hasTokenRefreshedHeader && !RequestsProcessor.pageReloadScheduled) {
        RequestsProcessor.pageReloadScheduled = true;
        RequestsProcessor.pageReloadScheduledAttempts = 0;
        RequestsProcessor.waitForRequests();
      }
    }
  }

  static requestsCounterInc(silentRequest: boolean) {
    if (!silentRequest) {
      RequestsProcessor.requestsCounter++;
    }
  }

  static requestsCounterDec(silentRequest: boolean) {
    if (!silentRequest && RequestsProcessor.requestsCounter > 0) {
      RequestsProcessor.requestsCounter--;
    }
  }

  static waitForRequests() {
    setTimeout(() => {
      RequestsProcessor.pageReloadScheduledAttempts++;
      let isLoading = RequestsProcessor.requestsCounter > 0;
      if (RequestsProcessor.pageReloadScheduledAttempts <= 30 && isLoading) {
        RequestsProcessor.waitForRequests();
      } else {
        var event = new Event('requestPageReload');
        window.dispatchEvent(event);
      }
    }, 1000);
  }

}
